import { DataClientFactory } from '../data/client'
import { AzureKeyCredential, SearchClient } from '@azure/search-documents'
import type { KontentPageRoute } from '~/types/KontentPageRoute'
import type { OpenPositionIndex, InsightIndex, PeopleIndex } from '~/types/PageIndex'
import getAllPages from './pages'
import type { IContentItem } from '@kontent-ai/delivery-sdk'
import { firstOrDefault } from '~/utils/first-or-default'
import type { InsightPageModel } from '~/models'

export const indexNameMappings: Record<string, string> = {
  insight_page: 'pages-index',
  people_page: 'people-index',
  career_opportunities_page: 'open-positions-index'
}

export async function rebuildAllPages() {
  console.log('Start rebuilding all pages..')
  const pages = await getAllPages()
  let msg = ''

  //const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i]
    const indexName = indexNameMappings[page.type]
    if (indexName) {
      msg += await updateSearchIndex(page.itemid, indexName)
      console.log(`Items left to process: ${pages.length - (i + 1)}`)
    }
  }
  console.log('Rebuild all pages done.')
}

export async function updateSearchIndex(itemid: string, indexName: string): Promise<string> {
  const config = useRuntimeConfig()
  const dataClient = DataClientFactory.kontentDeliveryClient(config, true)
  try {
    const endpoint = config.searchServiceUrl
    const apiKey = config.searchApiKey
    const response = await dataClient
      .items()
      .equalsFilter('system.id', itemid)
      .queryConfig({ waitForLoadingNewContent: true })
      .depthParameter(0)
      .toPromise()

    if (response.data.items.length > 0) {
      const item = response.data.items[0]
      const document = await transformToDocument(item, indexName)
      if (document) {
        const searchClient = new SearchClient<any>(
          endpoint,
          indexName,
          new AzureKeyCredential(apiKey)
        )
        const uploadResult = await searchClient.mergeOrUploadDocuments([document])

        if (uploadResult.results.length > 0) {
          console.log(`Uploaded document ${indexName}: ${item.system.id}`)
        }
      } else {
        return `No document found for ${itemid}`
      }
    }
  } catch (error: any) {
    console.log(error)
    return error.message
  }

  return ''
}

async function transformToDocument(item: any, indexName: string): Promise<any | null> {
  try {
    switch (indexName) {
      case 'pages-index':
        const ventureServices = item?.elements.ventureServices?.value?.map((term: any) => term.name)
        const ventureIndustries = item?.elements.ventureIndustries?.value?.map(
          (term: any) => term.name
        )

        const insight = {
          id: item.system.id,
          title: item.elements?.title?.value,
          subTitle: item.elements?.subtitle?.value,
          shortDescription: removeHtmlTags(item.elements?.shortDescription?.value),
          authors: await getPeopleLinkedItem(item.elements.authors?.value),
          speakers: await getPeopleLinkedItem(item.elements.speakers?.value),
          presenters: await getPeopleLinkedItem(item.elements.presenters?.value),
          relatedPeople: await getPeopleLinkedItem(item.elements.relatedPeople?.value),
          contentType: item.system?.type,
          content: removeHtmlTags(item.elements?.content?.value),
          urlSlug: item.elements?.urlSlug?.value,
          type: item.system?.type,
          insightCategory: extractTaxonomyValues(item, 'insightCategory'),
          capability: extractTaxonomyValues(item, 'relatedCapabilities'),
          publishingDate: getPublishingDate(item),
          timezonePublishingDate: item.elements?.publishingDate?.displayTimeZone,
          imgSrc: item?.elements?.cardImage?.value[0]?.url ?? extractImageFromItem(item),
          seoMetaTags: item.elements?.seoMetadataMetaKeywords?.value,
          richTextCustomElement: removeHtmlTags(item.elements?.richTextInterpreterRichText?.value),
          showOnManattVentures: ventureServices.length > 0 || ventureIndustries.length > 0,
          ventureServices: ventureServices,
          ventureIndustries: ventureIndustries
        } as InsightIndex

        return insight

      case 'people-index':
        const education = await extractEducation(item.system.id)
        return {
          id: item.system.id,
          title: item.elements.title?.value,
          bio: item.elements.contentBio?.value,
          firstName: item.elements.firstName?.value,
          lastName: item.elements.lastName?.value,
          lastNameLower: (item.elements.lastName?.value ?? '').toLowerCase(),
          alias: item.elements.peopleAlias?.value,
          location: item.elements.peopleLocation?.value,
          urlSlug: item.elements.urlSlug?.value,
          peopleImageUrl: firstOrDefault(item.elements.peopleImage.value)?.url,
          type: item.system?.type,
          primaryCapability: extractTaxonomyValues(item, 'contentPrimaryCapabilities'),
          specificCapability: extractTaxonomyValues(item, 'contentCapabilities'),
          displayName: item.elements.displayName?.value,
          position: extractTaxonomyValues(item, 'peopleTitle'),
          email: removeHtmlTags(item.elements?.email.value),
          phone: removeHtmlTags(item.elements?.peoplePhoneNumber?.value),
          seoMetaTags: item.elements?.seoMetadataMetaKeywords?.value,
          richTextCustomElement: removeHtmlTags(item.elements?.richTextInterpreterRichText?.value),
          education: education,
          vCardUrl: item.elements.vcard?.value[0]?.url ?? '',
          cardAddressDisplayName: item.elements.cardAddressDisplayName?.value
        } as PeopleIndex

      case 'open-positions-index':
        return {
          id: item.system.id,
          title: item.elements?.title?.value,
          urlSlug: item.elements?.urlSlug?.value,
          department: firstOrDefault(item.elements?.department?.value)?.name,
          location: firstOrDefault(item.elements?.location?.value),
          jobNumber: item.elements.jobNumber?.value?.toString() || '',
          content: removeHtmlTags(item.elements?.content?.value), ///custom element
          shortDescription: removeHtmlTags(item.elements?.shortDescription?.value), ///custom element
          type: item.system?.type,
          category: extractTaxonomyValues(item, 'careerCategory'),
          seoMetaTags: item.elements?.seoMetadataMetaKeywords?.value
        } as OpenPositionIndex
      default:
        return null
    }
  } catch (error) {
    console.error(`Error fetching page: ${item.system.codename}`, error)
    return null
  }
}

function extractTaxonomyValues(item: IContentItem, key: string): string[] {
  const element = item.elements[key]
  if (element && element.type === 'taxonomy' && Array.isArray(element.value)) {
    return element.value.map((taxonomy) => taxonomy.name)
  }
  return []
}

export function extractImageFromItem(item: IContentItem): string {
  const imageUrlMatch = item.elements.content?.value?.match(/<img\s+src="([^"]+)"/)
  if (imageUrlMatch) {
    return imageUrlMatch[1]
  } else {
    return ''
  }
}

async function getPeopleLinkedItem(codename: string) {
  const config = useRuntimeConfig()
  const client = DataClientFactory.kontentDeliveryClient(config, true)
  const arrOfNames: any[] = []
  if (!codename) return arrOfNames
  for (const code of codename) {
    if (code) {
      const response = await client.items().equalsFilter('system.codename', code).toPromise()
      const person = firstOrDefault(response.data.items)
      if (person) {
        arrOfNames.push(person?.elements.firstName.value + ' ' + person?.elements.lastName.value)
      }
    }
  }
  return arrOfNames
}

export async function deleteSearchIndex(itemid: string, indexName: string) {
  const config = useRuntimeConfig()
  const endpoint = config.searchServiceUrl
  const apiKey = config.searchApiKey
  const searchClient = new SearchClient<any>(endpoint, indexName, new AzureKeyCredential(apiKey))
  let msg = ''

  try {
    const document = await searchClient.getDocument(itemid)
    const result = await searchClient.deleteDocuments([document])
    result.results.forEach((result) => {
      if (result.succeeded) {
        msg += `Deleted document: ${result.key} \n`
        console.log(`Deleted document: ${result.key}`)
      } else {
        msg += `Failed to delete document: ${result.key} \n`
        console.error(`Failed to delete document: ${result.key}`)
      }
    })
  } catch (e) {
    console.log(e)
    msg += `Failed to delete document: ${itemid} \n`
  }

  return msg
}

async function getCachedPages() {
  const response = await $fetch('/api/navigation/pages')
  if (response.data) {
    return response.data as KontentPageRoute[]
  }
}
export function removeHtmlTags(inputString: string | undefined): string {
  if (!inputString) return ''
  return inputString.replace(/(<([^>]+)>)/gi, '')
}

function getPublishingDate(item: InsightPageModel): string {
  const startDateCategories = ['Events', 'Webinars']

  if (startDateCategories.includes(extractTaxonomyValues(item, 'insightCategory')[0] ?? '')) {
    return item.elements.eventStart.value ?? ''
  }
  return item.elements.publishingDate.value ?? ''
}

async function extractEducation(itemid: any): Promise<string> {
  const config = useRuntimeConfig()
  const deliveryClient = DataClientFactory.kontentDeliveryClient(config, false)

  const people = await deliveryClient
    .items()
    .equalsFilter('system.id', itemid)
    .queryConfig({ waitForLoadingNewContent: true })
    .depthParameter(8)
    .toPromise()

  const item = people.data.items[0]
  const linkedItems = people.data.linkedItems

  const linkedItemCodenames = (item.elements.contentRelatedContent as any).linkedItemCodenames

  let value = ''

  for (let i = 0; i < linkedItemCodenames.length; i++) {
    const codename = linkedItemCodenames[i]
    const linkedItem = linkedItems[codename]

    if (
      linkedItem.system.type === 'content_block' &&
      linkedItem.elements.title?.value === 'Education'
    ) {
      value = linkedItem.elements.content.value
      break
    }
  }
  return value
}
